/**
 * GDPR Article 7 + 9 — granular consent management.
 *
 * Patients toggle 7 individual consents (marketing email, AI scribe,
 * anonymised research, GP outcome share, internal audit, photos/video,
 * emergency contact). Each grant/withdrawal writes an audit row server-side
 * with IP, timestamp, and version.
 */
import React, { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { api } from '../../../lib/api';
import { t } from '../../../lib/i18n';

export function ConsentPrefs({ locale = 'en' }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get('/auth/me/consents').then(r => setData(r.data)).catch(() => setData({ consents: [] }));
  }, []);

  if (!data) return null;

  const toggle = async (key, current) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      await api.post('/auth/me/consents', { consent_key: key, granted: !current });
      const r = await api.get('/auth/me/consents');
      setData(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  return (
    <div className="surface border border-soft rounded-lg p-4 mt-6 max-w-xl" data-testid="consent-prefs-zone">
      <h3 className="font-heading font-semibold flex items-center gap-2 mb-1">
        <Lock className="w-4 h-4 text-brand"/> {t(locale, 'privacy_preferences')}
      </h3>
      <p className="text-sm text-fg-muted mb-3">{t(locale, 'privacy_preferences_lede')}</p>
      <div className="space-y-2.5" data-testid="consent-list">
        {(data.consents || [])
          // Iter 133 — Filter out wellness-category consents from the
          // Account & Privacy → Preferences panel. Those consents (Fitness,
          // Massage) now live inline on their respective service pages
          // (PatientFitness / MassageIntake) since they're bound to a
          // separate GDPR basis + waiver.
          .filter(c => c.category !== 'wellness')
          .map(c => (
          <div key={c.key} className="flex items-start justify-between gap-3 border border-soft rounded-md p-3" data-testid={`consent-row-${c.key}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{c.label}</div>
              <div className="text-xs text-fg-muted mt-0.5">{c.description}</div>
              {c.granted_at && c.granted && (
                <div className="text-[10px] text-fg-muted mt-1">{t(locale, 'consent_granted')}: {new Date(c.granted_at).toLocaleDateString()}</div>
              )}
              {c.withdrawn_at && !c.granted && (
                <div className="text-[10px] text-fg-muted mt-1">Withdrawn: {new Date(c.withdrawn_at).toLocaleDateString()}</div>
              )}
            </div>
            <button onClick={() => toggle(c.key, c.granted)}
                    disabled={!!busy[c.key]}
                    role="switch" aria-checked={c.granted}
                    data-testid={`consent-toggle-${c.key}`}
                    className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition ${c.granted ? 'bg-brand' : 'bg-fg-muted/30'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${c.granted ? 'translate-x-6' : 'translate-x-1'}`}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
